import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { getThemeById } from '../../config/themes'
import type { TenantThemeConfig } from '../../types/theme'
import { getMonthlyRewardPromotionConfig } from '../../services/promotionConfigService'
import type { MonthlyRewardTabKey } from '../../services/monthlyRewardService'
import { getEnabledMonthlyRewardTabs, isMonthlyRewardConfigUpdate } from '../../utils/monthlyRewardTabs'
import './ModernRewardPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface RewardOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  getAssetUrl: (path: string) => string
}

interface ModernRewardLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const TAB_ICONS: Record<MonthlyRewardTabKey, string> = {
  cashback: 'fas fa-hand-holding-usd',
  loss: 'fas fa-chart-line',
  refer: 'fas fa-user-friends',
  rank: 'fas fa-trophy',
}

const TAB_SHORT: Record<MonthlyRewardTabKey, string> = {
  cashback: 'เงินคืนสูงสุด',
  loss: 'ยอดเสียสูงสุด',
  refer: 'แนะนำเพื่อน',
  rank: 'นักเล่น Rank',
}

function getActiveTab(pathname: string): MonthlyRewardTabKey | null {
  const match = pathname.match(/\/reward\/([^/]+)/)
  const tab = match?.[1]
  if (tab === 'cashback' || tab === 'loss' || tab === 'refer' || tab === 'rank') {
    return tab
  }
  return null
}

/** Layout หน้ารางวัลประจำเดือน — ธีม HTD */
const ModernRewardLayout: React.FC<ModernRewardLayoutProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const [tabSettings, setTabSettings] = useState<Partial<Record<MonthlyRewardTabKey, { enabled?: boolean; title?: string }>>>({})
  const [moduleEnabled, setModuleEnabled] = useState(true)
  const [disabledTitle, setDisabledTitle] = useState('ปิดใช้งานชั่วคราว')
  const [disabledMessage, setDisabledMessage] = useState('ระบบรางวัลประจำเดือนปิดใช้งานชั่วคราว')
  const [loadingTabs, setLoadingTabs] = useState(true)

  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }
  const context: RewardOutletContext = { themeConfig, assetConfig, getAssetUrl }

  const loadTabSettings = useCallback(async () => {
    try {
      const response = await getMonthlyRewardPromotionConfig()
      if (response.success && response.data) {
        setModuleEnabled(response.data.enabled !== false)
        const display = response.data.settings?.display
        if (typeof display?.disabledTitle === 'string' && display.disabledTitle.trim()) {
          setDisabledTitle(display.disabledTitle)
        }
        if (typeof display?.disabledMessage === 'string' && display.disabledMessage.trim()) {
          setDisabledMessage(display.disabledMessage)
        }
        if (response.data.settings?.tabs) {
          setTabSettings(response.data.settings.tabs)
        }
      }
    } catch {
      setTabSettings({})
      setModuleEnabled(true)
    } finally {
      setLoadingTabs(false)
    }
  }, [])

  useEffect(() => {
    void loadTabSettings()
  }, [loadTabSettings])

  useEffect(() => {
    const handleRewardRefresh = (event: Event) => {
      const detail = (event as CustomEvent)?.detail
      if (!isMonthlyRewardConfigUpdate(detail)) return
      void loadTabSettings()
    }

    window.addEventListener('content_update', handleRewardRefresh as EventListener)
    window.addEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)

    return () => {
      window.removeEventListener('content_update', handleRewardRefresh as EventListener)
      window.removeEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)
    }
  }, [loadTabSettings])

  const enabledTabs = useMemo(() => getEnabledMonthlyRewardTabs(tabSettings), [tabSettings])
  const activeTab = getActiveTab(location.pathname)

  useEffect(() => {
    if (loadingTabs || enabledTabs.length === 0) return
    if (!activeTab || !enabledTabs.some((tab) => tab.key === activeTab)) {
      navigate(`/reward/${enabledTabs[0].to}`, { replace: true })
    }
  }, [activeTab, enabledTabs, loadingTabs, navigate])

  if (!loadingTabs && (!moduleEnabled || enabledTabs.length === 0)) {
    return (
      <div className="content htd-reward">
        <div className="htd-reward__glow" aria-hidden />
        <div className="htd-reward__shell">
          <h4>{disabledTitle}</h4>
          <p>{disabledMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="content htd-reward">
      <div className="htd-reward__glow" aria-hidden />
      <div className="htd-reward__inner">
        <header className="htd-reward__hero htd-reward-anim htd-reward-anim--1">
          <div className="htd-reward__badge" aria-hidden>
            <i className="fas fa-trophy" />
          </div>
          <div className="htd-reward__hero-text">
            <p className="htd-reward__eyebrow">Monthly Reward</p>
            <h1 className="htd-reward__title">รางวัลประจำเดือน</h1>
            <p className="htd-reward__desc">
              ดูอันดับผู้เล่นเด่นแต่ละหมวด รับรางวัลพิเศษตามผลงานประจำเดือน
            </p>
          </div>
        </header>

        <nav className="htd-reward__tabs htd-reward-anim htd-reward-anim--2" aria-label="หมวดรางวัลประจำเดือน">
          {enabledTabs.map((tab) => (
            <NavLink
              key={tab.key}
              to={tab.to}
              end={tab.to === 'cashback'}
              className={({ isActive }) => `htd-reward__tab${isActive ? ' is-active' : ''}`}
              title={tab.label}
            >
              <span className="htd-reward__tab-icon" aria-hidden>
                <i className={TAB_ICONS[tab.key]} />
              </span>
              <span className="htd-reward__tab-label">{TAB_SHORT[tab.key] || tab.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="htd-reward-anim htd-reward-anim--3">
          {loadingTabs ? (
            <div className="htd-reward__shell">กำลังโหลด...</div>
          ) : (
            <Outlet context={context} />
          )}
        </div>
      </div>
    </div>
  )
}

export default ModernRewardLayout
